import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const PROCESS_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/process#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/process`,
      "name": "Our Process | DVS Power Washing Dallas TX",
      "description": "Learn how DVS Power Washing works — from free quote to final walkthrough. Simple, transparent, and professional pressure washing process in Dallas Metroplex, TX.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Our Process", "item": `${import.meta.env.VITE_SITE_URL}/process` }
        ]
      }
    },
    {
      "@type": "HowTo",
      "name": "How DVS Power Washing Works",
      "description": "Our simple 4-step pressure washing process from free quote to 100% satisfaction guarantee.",
      "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Request a Free Quote",
          "text": "Contact us by phone or through our website. We'll discuss your needs and provide a free, no-obligation estimate."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "On-Site Evaluation",
          "text": "We visit your property to assess the scope of work and provide an accurate quote tailored to your specific needs."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Professional Pressure Washing",
          "text": "Our experienced team arrives on time with professional equipment and completes the job efficiently and safely."
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Final Walkthrough & Satisfaction Guarantee",
          "text": "We walk through the completed work with you to ensure you're 100% satisfied with the results before we leave."
        }
      ]
    }
  ]
};

const ProcessPage = () => {
  useEffect(() => {
    // Canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://dvspowerwashingdallas.com/process';

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-process';
    script.text = JSON.stringify(PROCESS_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-process');
      if (el) el.remove();
    };
  }, []);

  const steps = [
    {
      number: '1',
      title: 'Request a Free Quote',
      description: 'Contact us by phone or through our website. We\'ll discuss your needs and provide a free, no-obligation estimate.',
      icon: 'ri-phone-fill',
    },
    {
      number: '2',
      title: 'On-Site Evaluation',
      description: 'We visit your property to assess the scope of work and provide an accurate quote tailored to your specific needs.',
      icon: 'ri-map-pin-fill',
    },
    {
      number: '3',
      title: 'Professional Pressure Washing',
      description: 'Our experienced team arrives on time with professional equipment and completes the job efficiently and safely.',
      icon: 'ri-tools-fill',
    },
    {
      number: '4',
      title: 'Final Walkthrough & Satisfaction Guarantee',
      description: 'We walk through the completed work with you to ensure you\'re 100% satisfied with the results before we leave.',
      icon: 'ri-checkbox-circle-fill',
    },
  ];

  const areas = [
    'Dallas',
    'Fort Worth',
    'Plano',
    'Irving',
    'Frisco',
    'McKinney',
    'Arlington',
    'Garland',
    'Richardson',
    'Grand Prairie',
    'Carrollton',
    'Denton',
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative h-[400px] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E3A5D] to-[#1A5280]"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">Our Process</h1>
          <p className="text-xl lg:text-2xl text-[#4FB3D9]">
            Simple, transparent, and professional
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] text-center mb-20">
            Our Simple Process
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gray-300 -z-10"></div>

            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-32 h-32 mx-auto bg-[#4FB3D9] rounded-full flex items-center justify-center text-white text-6xl font-bold shadow-xl">
                    {step.number}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mb-4 px-4">{step.title}</h3>
                <p className="text-base text-[#5A6C7D] leading-relaxed mb-6 px-2">
                  {step.description}
                </p>

                <div className="w-12 h-12 flex items-center justify-center mx-auto">
                  <i className={`${step.icon} text-5xl text-[#0E3A5D]`}></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] text-center mb-12">
            Areas We Serve
          </h2>

          <div className="flex justify-center mb-12">
            <div className="w-full max-w-2xl rounded-xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d429155.68!2d-96.7970!3d32.7767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c19f77b45974b%3A0xb9ec9ba4f647678f!2sDallas%2C%20TX!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area Map - Dallas Metroplex, Texas"
              ></iframe>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-center mb-10">
            {areas.map((area, index) => (
              <div key={index} className="text-lg text-gray-700 font-medium">
                {area}
              </div>
            ))}
          </div>

          <p className="text-lg text-[#5A6C7D] text-center">
            Don't see your area? <a href="tel:2146368343" className="text-[#0E3A5D] font-semibold hover:text-[#4FB3D9]">Call us!</a> We may still be able to help.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-28 h-28 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-9xl text-[#0E3A5D]"></i>
            </div>

            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] mb-6">
                100% Satisfaction Guarantee
              </h2>
              <p className="text-lg text-[#5A6C7D] leading-relaxed mb-6">
                We stand behind our work with a complete satisfaction guarantee. If you're not happy with the results, we'll make it right—no questions asked. Your trust and satisfaction are our top priorities, and we won't consider the job done until you're completely satisfied.
              </p>
              <p className="text-lg text-[#4FB3D9] italic font-medium">
                "We don't cut corners — we clean them."
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0E3A5D]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Experience professional service with military precision
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="tel:2146368343"
              className="bg-[#4FB3D9] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#3A9DC4] transition-all whitespace-nowrap flex items-center space-x-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>Call (214) 636-8343</span>
            </a>
            <Link
              to="/contact"
              className="bg-white text-[#0E3A5D] px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all whitespace-nowrap w-full sm:w-auto text-center"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProcessPage;
